import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { 
  ArrowLeft, Search, Star, Ban, Trash2, MessageSquare, 
  User, Phone, Mail, MoreVertical, X, Share2, Loader2, Shield
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useClickLock } from '../hooks';
import { InviteFriendDialog } from '@/components/InviteFriendDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format last seen time
const formatLastSeen = (lastSeenStr) => {
  if (!lastSeenStr) return 'Never';
  
  const lastSeen = new Date(lastSeenStr);
  const now = new Date();
  const diffMs = now - lastSeen;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return lastSeen.toLocaleDateString();
};

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [blockedContacts, setBlockedContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showBlockedDialog, setShowBlockedDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'favorites'
  
  // Add contact form
  const [newContactPhone, setNewContactPhone] = useState('');
  const [newContactEmail, setNewContactEmail] = useState('');
  const [newContactNickname, setNewContactNickname] = useState('');
  
  // Add friend state - search for existing users
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addingUserId, setAddingUserId] = useState(null);
  
  // Unified Invite friend dialog
  const [showInviteFriendDialog, setShowInviteFriendDialog] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const token = localStorage.getItem('token');
      const [contactsRes, blockedRes] = await Promise.all([
        axios.get(`${API}/contacts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/contacts/blocked`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setContacts(contactsRes.data);
      setBlockedContacts(blockedRes.data);
    } catch (error) {
      console.error('Failed to fetch contacts', error);
    }
  };

  const addContact = async () => {
    if (!newContactPhone && !newContactEmail) {
      toast.error('Please enter phone or email');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/contacts`, {
        phone: newContactPhone || null,
        email: newContactEmail || null,
        nickname: newContactNickname || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contact added!');
      setShowInviteDialog(false);
      setNewContactPhone('');
      setNewContactEmail('');
      setNewContactNickname('');
      fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add contact');
    } finally {
      setLoading(false);
    }
  };

  // Search for existing Privluxe users
  const searchUsers = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/contacts/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data || []);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (userSearchQuery) {
        searchUsers(userSearchQuery);
      } else {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearchQuery]);

  // Add user as friend
  const addFriend = async (userId, username) => {
    setAddingUserId(userId);
    try {
      const token = localStorage.getItem('token');
      // Start a conversation which auto-adds as contact
      await axios.post(`${API}/conversations/start`, {
        user_id: userId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${username} added as friend!`);
      setShowInviteDialog(false);
      setUserSearchQuery('');
      setSearchResults([]);
      fetchContacts(); // Refresh contacts list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add friend');
    } finally {
      setAddingUserId(null);
    }
  };

  const toggleFavorite = async (contactId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/contacts/${contactId}/favorite`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchContacts();
    } catch (error) {
      toast.error('Failed to update favorite status');
    }
  };

  const blockContact = async (contactId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/contacts/${contactId}/block`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contact blocked');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to block contact');
    }
  };

  const unblockContact = async (contactId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/contacts/${contactId}/unblock`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contact unblocked');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to unblock contact');
    }
  };

  const deleteContact = async (contactId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contact removed');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to remove contact');
    }
  };

  const startChat = async (contactUserId) => {
    try {
      const token = localStorage.getItem('token');
      // Check if conversation exists or create new one
      const response = await axios.post(`${API}/conversations/start`, {
        user_id: contactUserId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate(`/chat/${response.data.conversation_id}`);
    } catch (error) {
      toast.error('Failed to start conversation');
    }
  };

  const filteredContacts = contacts.filter(contact => {
    const info = contact.contact_info;
    // Search by username only - no phone search for privacy
    const matchesSearch = 
      (info.username?.toLowerCase().includes(searchQuery.toLowerCase()));
    
    if (activeTab === 'favorites') {
      return matchesSearch && contact.is_favorite;
    }
    return matchesSearch;
  });

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#020617' }} data-testid="contacts-page">
      {/* Header */}
      <header 
        className="sticky top-0 z-10 border-b safe-area-top"
        style={{ 
          background: 'rgba(10, 15, 44, 0.95)',
          backdropFilter: 'blur(20px)',
          borderColor: 'rgba(255, 255, 255, 0.05)'
        }}
      >
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="text-gray-400 hover:text-white hover:bg-white/10"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-lg font-semibold" style={{ color: '#E5E7EB' }}>Friends</h1>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShowBlockedDialog(true)}
              className="text-gray-400 hover:text-white hover:bg-white/10"
            >
              <Ban className="h-4 w-4 mr-1" />
              Blocked ({blockedContacts.length})
            </Button>
            
            {/* Add Friend Dialog - Search for existing users */}
            <Dialog 
              open={showInviteDialog} 
              onOpenChange={(open) => {
                setShowInviteDialog(open);
                if (!open) {
                  setUserSearchQuery('');
                  setSearchResults([]);
                }
              }}
            >
              <DialogTrigger asChild>
                <Button 
                  size="sm"
                  className="text-white"
                  style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
                  data-testid="friends-add-button"
                >
                  <User className="h-4 w-4 mr-1" /> Add Friend
                </Button>
              </DialogTrigger>
              <DialogContent 
                className="max-w-[calc(100%-2rem)] sm:max-w-md mx-auto border-0 max-h-[90vh] overflow-y-auto"
                style={{ 
                  background: 'rgba(10, 15, 44, 0.98)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
                data-testid="add-friend-dialog"
              >
                <DialogHeader>
                  <DialogTitle style={{ color: '#E5E7EB' }}>Add Friend</DialogTitle>
                  <DialogDescription style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
                    Search for Privluxe users by username
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 mt-4">
                  {/* Search Input */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      placeholder="Enter username..."
                      className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12"
                      autoFocus
                      data-testid="friend-search-input"
                    />
                  </div>

                  {/* Search Results */}
                  <div className="min-h-[200px] max-h-[300px] overflow-y-auto">
                    {searchLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-purple-400" />
                      </div>
                    ) : searchResults.length > 0 ? (
                      <div className="space-y-2">
                        {searchResults.map((user) => (
                          <div 
                            key={user.id}
                            className="flex items-center justify-between p-3 rounded-lg transition-colors hover:bg-white/5"
                            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
                          >
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                                style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
                              >
                                {user.username?.[0]?.toUpperCase() || '?'}
                              </div>
                              <div>
                                <p className="text-white font-medium">{user.username}</p>
                                {user.phone && (
                                  <p className="text-xs text-gray-400">{user.phone}</p>
                                )}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => addFriend(user.id, user.username)}
                              disabled={addingUserId === user.id}
                              className="text-white"
                              style={{ background: 'rgba(124, 58, 237, 0.3)', border: '1px solid rgba(124, 58, 237, 0.5)' }}
                              data-testid={`add-friend-${user.username}`}
                            >
                              {addingUserId === user.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>Add</>
                              )}
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : userSearchQuery.length >= 2 ? (
                      <div className="text-center py-8">
                        <User className="h-12 w-12 mx-auto mb-3 text-gray-500" />
                        <p className="text-gray-400">No users found</p>
                        <p className="text-xs text-gray-500 mt-1">Try a different search term</p>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Search className="h-12 w-12 mx-auto mb-3 text-gray-500" />
                        <p className="text-gray-400">Search for friends</p>
                        <p className="text-xs text-gray-500 mt-1">Enter at least 2 characters</p>
                      </div>
                    )}
                  </div>

                  {/* Privacy Notice */}
                  <div 
                    className="p-3 rounded-lg text-xs flex items-center gap-2"
                    style={{ background: 'rgba(124, 58, 237, 0.1)', border: '1px solid rgba(124, 58, 237, 0.2)' }}
                  >
                    <Shield className="h-4 w-4 shrink-0" style={{ color: '#A78BFA' }} />
                    <span style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
                      Search by username only. Email and phone are private.
                    </span>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        
        {/* Search and tabs */}
        <div className="max-w-4xl mx-auto px-4 pb-3">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search contacts..."
              className="pl-10 bg-white/5 border-white/10 text-white h-10"
            />
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant={activeTab === 'all' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('all')}
              className={activeTab === 'all' ? 'bg-purple-500 text-white' : 'text-gray-400'}
            >
              All ({contacts.length})
            </Button>
            <Button 
              variant={activeTab === 'favorites' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('favorites')}
              className={activeTab === 'favorites' ? 'bg-purple-500 text-white' : 'text-gray-400'}
            >
              <Star className="h-3 w-3 mr-1" />
              Favorites
            </Button>
          </div>
        </div>
      </header>

      {/* Contact list */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-4 space-y-2">
          {filteredContacts.length === 0 ? (
            <div className="text-center py-12">
              <User className="h-12 w-12 mx-auto mb-4 text-gray-500" />
              <p className="text-gray-400">
                {activeTab === 'favorites' ? 'No favorite contacts' : 'No friends yet'}
              </p>
              <Button 
                onClick={() => setShowInviteFriendDialog(true)}
                variant="ghost"
                className="mt-4 text-purple-400"
                data-testid="invite-first-friend-btn"
              >
                <Share2 className="h-4 w-4 mr-1" /> Invite your first friend
              </Button>
            </div>
          ) : (
            filteredContacts.map(contact => (
              <Card 
                key={contact.id}
                className="border-0 cursor-pointer transition-all hover:bg-white/5"
                style={{ background: 'rgba(255,255,255,0.02)' }}
              >
                <CardContent className="p-3">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      {contact.contact_info.profile_picture ? (
                        <img 
                          src={contact.contact_info.profile_picture} 
                          alt={contact.contact_info.username}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div 
                          className="w-12 h-12 rounded-full flex items-center justify-center"
                          style={{ background: 'rgba(124, 58, 237, 0.2)' }}
                        >
                          <span style={{ color: '#7C3AED' }} className="text-lg">
                            {(contact.contact_info.username || '?').charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      {/* Online indicator */}
                      {contact.contact_info.is_online && (
                        <div 
                          className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-green-500 border-2" 
                          style={{ borderColor: '#020617' }}
                        />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium truncate" style={{ color: '#E5E7EB' }}>
                          {contact.contact_info.username || 'Unknown'}
                        </p>
                        {contact.is_favorite && (
                          <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                        )}
                      </div>
                      <p className="text-xs truncate" style={{ color: 'rgba(229,231,235,0.5)' }}>
                        {contact.contact_info.is_online 
                          ? 'Online' 
                          : `Last seen ${formatLastSeen(contact.contact_info.last_seen)}`
                        }
                      </p>
                      {/* REMOVED: Phone number display - privacy protection */}
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => startChat(contact.contact_user_id)}
                        className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-400/10"
                      >
                        <MessageSquare className="h-5 w-5" />
                      </Button>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white">
                            <MoreVertical className="h-5 w-5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48 bg-slate-900 border-slate-800">
                          <DropdownMenuItem 
                            onClick={() => toggleFavorite(contact.id)} 
                            className="text-gray-300 focus:text-white focus:bg-white/10"
                          >
                            <Star className={`h-4 w-4 mr-2 ${contact.is_favorite ? 'fill-yellow-400 text-yellow-400' : ''}`} />
                            {contact.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => blockContact(contact.id)} 
                            className="text-gray-300 focus:text-white focus:bg-white/10"
                          >
                            <Ban className="h-4 w-4 mr-2" />
                            Block contact
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => deleteContact(contact.id)} 
                            className="text-red-400 focus:text-red-300 focus:bg-red-500/10"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Remove contact
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Blocked contacts dialog */}
      <Dialog open={showBlockedDialog} onOpenChange={setShowBlockedDialog}>
        <DialogContent 
          className="max-w-md border-0"
          style={{ 
            background: 'rgba(10, 15, 44, 0.98)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: '#E5E7EB' }}>Blocked Contacts</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-4 max-h-[400px] overflow-y-auto">
            {blockedContacts.length === 0 ? (
              <p className="text-center py-8 text-gray-400">No blocked contacts</p>
            ) : (
              blockedContacts.map(contact => (
                <div 
                  key={contact.id}
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ background: 'rgba(255,255,255,0.03)' }}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ background: 'rgba(239,68,68,0.2)' }}
                    >
                      <Ban className="h-5 w-5 text-red-400" />
                    </div>
                    <span style={{ color: '#E5E7EB' }}>
                      {contact.contact_info?.username || 'Unknown'}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => unblockContact(contact.id)}
                    className="text-green-400 hover:text-green-300"
                  >
                    Unblock
                  </Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Unified Invite Friend Dialog */}
      <InviteFriendDialog open={showInviteFriendDialog} onOpenChange={setShowInviteFriendDialog} />
    </div>
  );
}
